import React, { useState } from 'react'
import { Check, X, ExternalLink, MapPin, Users, Briefcase, Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { CandidateAction } from '../hooks/useProspectCandidates'
import { CRM_SYNC_ACTIONABLE, type CandidateCrmError, type CrmSyncStatus, type ProspectCandidate } from '../types'

type Props = {
  candidate: ProspectCandidate
  pending: boolean
  error: string | undefined
  onAction: (action: CandidateAction) => void
}

const CRM_LABELS: Record<CrmSyncStatus, string> = {
  creating: 'Creating in HubSpot…',
  created: 'Created in HubSpot',
  linked: 'Linked to existing HubSpot company',
  pending_approval: 'HubSpot company awaiting approval',
  disabled: 'Not added to HubSpot — company creation is Off in AI Permissions',
  not_connected: 'Not added to HubSpot — HubSpot is not connected',
  failed: 'HubSpot company could not be created',
  dismissed: 'HubSpot company dismissed',
}

const CRM_ERROR_LABELS: Record<CandidateCrmError, string> = {
  hubspot_error: 'HubSpot returned an error.',
  hubspot_reauth_required: 'Reconnect HubSpot in Settings → Integrations.',
  field_write_denied: 'Company name and domain are both blocked by AI Permissions.',
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** Only http(s) links are rendered as links — source URLs come from web search. */
function isSafeUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

const CandidateCard: React.FC<Props> = ({ candidate: c, pending, error, onAction }) => {
  const { currentTheme: t } = useTheme()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')

  const crmTone = (sync: CrmSyncStatus) =>
    sync === 'created' || sync === 'linked' ? { bg: t.successBackground, text: t.successText }
    : sync === 'failed' ? { bg: t.errorBackground, text: t.errorText }
    : sync === 'pending_approval' || sync === 'not_connected' ? { bg: t.warningBackground, text: t.warningText }
    : { bg: t.hoverBackground, text: t.secondrytext }

  const facts = [
    c.industry ? { icon: <Briefcase size={12} />, text: c.industry } : null,
    c.location ? { icon: <MapPin size={12} />, text: c.location } : null,
    c.headcountRange ? { icon: <Users size={12} />, text: `${c.headcountRange} employees` } : null,
  ].filter((f): f is { icon: React.ReactElement; text: string } => f !== null)

  const secondaryButton = { backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }

  return (
    <article
      className="rounded-xl p-4 flex flex-col gap-3"
      style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold truncate" style={{ color: t.text }}>{c.name}</h3>
          {/* The verified host — the run fetched https://<domain>/ to confirm it. */}
          <a
            href={`https://${c.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12.5px] hover:underline"
            style={{ color: t.accent }}
          >
            {c.domain}
            <ExternalLink size={11} />
          </a>
        </div>
        {c.status !== 'new' && (
          <span
            className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
            style={c.status === 'accepted'
              ? { backgroundColor: t.successBackground, color: t.successText }
              : { backgroundColor: t.errorBackground, color: t.errorText }}
          >
            {c.status === 'accepted' ? 'Accepted' : 'Rejected'}
          </span>
        )}
      </header>

      {facts.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {facts.map((f) => (
            <span key={f.text} className="inline-flex items-center gap-1 text-[12px]" style={{ color: t.secondrytext }}>
              {f.icon}
              {f.text}
            </span>
          ))}
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: t.mutedText }}>Why it fits</p>
        <p className="text-[13px] leading-relaxed" style={{ color: t.text }}>{c.fitReasoning}</p>
      </div>

      {c.likelyTitles.length > 0 && (
        <p className="text-[12.5px]" style={{ color: t.secondrytext }}>
          <span className="font-medium" style={{ color: t.text }}>Likely buyers: </span>
          {c.likelyTitles.join(', ')}
        </p>
      )}

      {c.sourceUrls.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: t.mutedText }}>Sources</p>
          <ul className="flex flex-wrap gap-1.5">
            {c.sourceUrls.filter(isSafeUrl).map((url) => (
              <li key={url}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={url}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] max-w-[260px] hover:underline"
                  style={{ backgroundColor: t.sectionBackground, color: t.text, border: `1px solid ${t.border}` }}
                >
                  <span className="truncate">{hostOf(url)}</span>
                  <ExternalLink size={11} className="shrink-0" style={{ color: t.mutedText }} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {c.status === 'rejected' && c.rejectReason && (
        <p className="text-[12.5px]" style={{ color: t.secondrytext }}>
          <span className="font-medium" style={{ color: t.text }}>Reason: </span>
          {c.rejectReason}
        </p>
      )}

      {c.status === 'accepted' && c.crmSync && (
        <div className="rounded-lg px-3 py-2 text-[12.5px]" style={{ backgroundColor: crmTone(c.crmSync).bg, color: crmTone(c.crmSync).text }}>
          {CRM_LABELS[c.crmSync]}
          {c.crmSync === 'failed' && c.crmError ? ` ${CRM_ERROR_LABELS[c.crmError]}` : ''}
        </div>
      )}

      {error && <p className="text-[12px]" style={{ color: t.errorText }} role="alert">{error}</p>}

      {c.status === 'new' && !rejecting && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            disabled={pending}
            onClick={() => onAction({ kind: 'accept' })}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: t.accentGradient ?? t.accent, color: t.buttonText }}
          >
            {pending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            Accept
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => setRejecting(true)}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            style={secondaryButton}
          >
            <X size={13} />
            Reject
          </button>
        </div>
      )}

      {c.status === 'new' && rejecting && (
        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-medium" style={{ color: t.secondrytext }} htmlFor={`reject-${c.id}`}>
            Reason (optional)
          </label>
          <input
            id={`reject-${c.id}`}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Too small, wrong region"
            className="h-9 rounded-lg px-3 text-[13px] outline-none"
            style={{ backgroundColor: t.inputBackground, color: t.text, border: `1px solid ${t.border}` }}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pending}
              onClick={() => onAction({ kind: 'reject', ...(reason.trim() ? { reason: reason.trim() } : {}) })}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: t.errorBackground, color: t.errorText }}
            >
              {pending ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
              Confirm reject
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => { setRejecting(false); setReason('') }}
              className="h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60"
              style={secondaryButton}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {c.status === 'accepted' && c.crmSync && CRM_SYNC_ACTIONABLE.includes(c.crmSync) && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            disabled={pending}
            onClick={() => onAction({ kind: 'approve_crm' })}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: t.accentGradient ?? t.accent, color: t.buttonText }}
          >
            {pending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            {c.crmSync === 'pending_approval' ? 'Approve & create in HubSpot' : 'Retry HubSpot'}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => onAction({ kind: 'dismiss_crm' })}
            className="h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            style={secondaryButton}
          >
            Don&apos;t add to HubSpot
          </button>
        </div>
      )}
    </article>
  )
}

export default React.memo(CandidateCard)
